// src/containers/FormBuilderDashboard/hooks/useFormEditor.js
import { useState, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  createCustomForm,
  updateCustomForm,
  deleteCustomForm,
  createCustomFormPropertyRaw,
  deleteCustomFormPropertyRaw,
  updateCustomFormOrder,
} from '../../../actions/apiCustomForm';
import { getCustomForms } from '../../../actions/apiGetInfo';
import { DEFAULT_PRINT_CRITERIA, parsePrintCriteria } from '../components/PrintCriteriaConfig';
import _ from 'lodash';

// Custom ID generator
const generateUniqueId = () => {
  return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
};

// Sanitize label to create a valid ID
const sanitizeLabelForId = (label, existingIds, counter) => {
  let id = label
    .toLowerCase()
    .replace(/[^a-z0-9]/g, '_')
    .replace(/_+/g, '_')
    .trim('_');
  id = id || 'section';
  let uniqueId = id;
  let suffix = counter;
  while (existingIds.includes(uniqueId)) {
    uniqueId = `${id}_${suffix++}`;
  }
  return uniqueId;
};

const useFormEditor = () => {
  const dispatch = useDispatch();
  const custom_forms = useSelector(state => state.settings_refresh?.custom_forms || []);
  const installation = useSelector(state => state.settings_dealerships?.installation || {});
  const default_profiles = useSelector(state => state.settings_users?.default_profiles || []);

  const [editingForm, setEditingForm] = useState(null);
  const [formName, setFormName] = useState('');
  const [sections, setSections] = useState({});
  const [headers, setHeaders] = useState({});
  const [formTotaling, setFormTotaling] = useState({
    approved: {
      enabled: true,
      equation: 'estimate_total',
      conditions: [{ header: 'approved', operator: 'equals', value: 'true' }],
      filters: [],
    },
    completed: {
      enabled: true,
      equation: 'estimate_total',
      conditions: [{ header: 'completed', operator: 'equals', value: 'true' }],
      filters: [],
    },
    pending: {
      enabled: true,
      equation: 'estimate_total',
      conditions: [
        { header: 'recommend', operator: 'equals', value: 'true' },
        { header: 'required', operator: 'equals', value: 'true', operatorType: 'or' },
        { header: 'approved', operator: 'equals', value: 'false' },
        { header: 'completed', operator: 'equals', value: 'false' },
      ],
      filters: [],
    },
    grand: {
      enabled: true,
      equation: 'estimate_total',
      conditions: [
        { header: 'recommend', operator: 'equals', value: 'true' },
        { header: 'required', operator: 'equals', value: 'true', operatorType: 'or' },
        { header: 'approved', operator: 'equals', value: 'true', operatorType: 'or' },
      ],
      filters: [],
    },
    parts: { enabled: true, equation: 'parts_total', conditions: [], filters: [] },
    labor: { enabled: true, equation: 'labor_total', conditions: [], filters: [] },
  });

  // NEW: send_sftp flag (default true for backward compatibility with old frontend)
  const [sendSftp, setSendSftp] = useState(true);
  // print_criteria: which mpi_work_items appear on the printed PDF
  const [printCriteria, setPrintCriteria] = useState(DEFAULT_PRINT_CRITERIA);

  const [sectionCounter, setSectionCounter] = useState(0);
  const [error, setError] = useState('');
  const [sectionModalOpen, setSectionModalOpen] = useState(false);
  const [newSectionName, setNewSectionName] = useState('');
  const [newFormModalOpen, setNewFormModalOpen] = useState(false);
  const [newFormName, setNewFormName] = useState('');
  const [saving, setSaving] = useState(false);
  const [isDataReady, setIsDataReady] = useState(false);
  const tableRefs = useRef({});

  const getDefaultInputType = (field) => {
    const booleanFields = [
      'show_mpi', 'active', 'deleted', 'auto_assign', 'pass', 'recommend', 'required',
      'approved', 'declined', 'completed', 'dvi_pdf_show', 'lock_pass', 'lock_recommend',
      'lock_required', 'lock_labor_rate', 'lock_labor_hours', 'lock_labor_total',
      'lock_parts', 'lock_parts_total', 'lock_quantity', 'lock_estimate_total',
      'lock_approved', 'lock_declined', 'lock_completed', 'lock_estimate', 'lock_counter',
    ];
    const userstampFields = [
      'pass', 'recommend', 'required', 'approved', 'declined', 'completed',
      'labor_rate', 'labor_hours', 'labor_total', 'parts', 'parts_total',
      'quantity', 'estimate_total', 'counter', 'left_front', 'right_front',
      'left_rear', 'right_rear', 'front_actual', 'front_min', 'rear_actual', 'rear_min',
    ];
    if (userstampFields.includes(field)) return 'userstamp';
    if (booleanFields.includes(field)) return 'checkbox';
    return 'text';
  };

  const resetState = () => {
    setEditingForm(null);
    setFormName('');
    setSections({});
    setHeaders({});
    setFormTotaling({
      approved: {
        enabled: true,
        equation: 'estimate_total',
        conditions: [{ header: 'approved', operator: 'equals', value: 'true' }],
        filters: [],
      },
      completed: {
        enabled: true,
        equation: 'estimate_total',
        conditions: [{ header: 'completed', operator: 'equals', value: 'true' }],
        filters: [],
      },
      pending: {
        enabled: true,
        equation: 'estimate_total',
        conditions: [
          { header: 'recommend', operator: 'equals', value: 'true' },
          { header: 'required', operator: 'equals', value: 'true', operatorType: 'or' },
          { header: 'approved', operator: 'equals', value: 'false' },
          { header: 'completed', operator: 'equals', value: 'false' },
        ],
        filters: [],
      },
      grand: {
        enabled: true,
        equation: 'estimate_total',
        conditions: [
          { header: 'recommend', operator: 'equals', value: 'true' },
          { header: 'required', operator: 'equals', value: 'true', operatorType: 'or' },
          { header: 'approved', operator: 'equals', value: 'true', operatorType: 'or' },
        ],
        filters: [],
      },
      parts: { enabled: true, equation: 'parts_total', conditions: [], filters: [] },
      labor: { enabled: true, equation: 'labor_total', conditions: [], filters: [] },
    });
    setSendSftp(true);                    // Reset to default true
    setPrintCriteria(DEFAULT_PRINT_CRITERIA);
    setSectionCounter(0);
    setError('');
    setIsDataReady(false);
    tableRefs.current = {};
  };

  const handleEdit = (form) => {
    if (!form?.id) return;

    resetState();
    setEditingForm(form);
    setFormName(form.name);
    setIsDataReady(false);

    const props = form.custom_form_properties || [];
    const loadedSections = {};
    const loadedHeaders = {};
    let counter = 0;
    const sectionItems = {};
    const existingIds = [];

    const sortedProps = _.sortBy(props, 'order');

    sortedProps.forEach(p => {
      if (p.key.match(/section_(.+)_id/)) {
        const sectionId = p.key.match(/section_(.+)_id/)?.[1];
        if (sectionId && sectionId !== 'undefined') {
          const labelProp = props.find(pr => pr.key === `section_${sectionId}_label`);
          const label = labelProp?.value || sectionId.replace(/_/g, ' ').toUpperCase();
          const sanitizedId = sanitizeLabelForId(label, existingIds, counter);
          existingIds.push(sanitizedId);
          loadedSections[sanitizedId] = {
            label,
            items: [],
            id: sanitizedId,
            totaling: { subtotal: false, equation: 'estimate_total' },
            layout: { width: '100%', align: 'left' },
            background: { color: '', image: '' },
            headerColor: '#f5f5f5',
            fontColor: '#000000',
            headerFontColor: '#000000',
          };
          loadedHeaders[sanitizedId] = [];
          counter++;
        }
      }
      else if (p.key.match(/section_(.+)_label/)) {
        const sectionId = p.key.match(/section_(.+)_label/)?.[1];
        const newId = Object.keys(loadedSections).find(id => id.startsWith(sectionId)) || sectionId;
        if (newId && newId !== 'undefined' && loadedSections[newId]) {
          loadedSections[newId].label = p.value || sectionId.replace(/_/g, ' ').toUpperCase();
        }
      }
      else if (p.key.match(/section_(.+)_layout/)) {
        const sectionId = p.key.match(/section_(.+)_layout/)?.[1];
        const newId = Object.keys(loadedSections).find(id => id.startsWith(sectionId)) || sectionId;
        if (newId && newId !== 'undefined' && loadedSections[newId]) {
          try {
            loadedSections[newId].layout = JSON.parse(p.value) || { width: '100%', align: 'left' };
          } catch (e) {
            loadedSections[newId].layout = { width: '100%', align: 'left' };
          }
        }
      }
      else if (p.key.match(/section_(.+)_totaling/)) {
        const sectionId = p.key.match(/section_(.+)_totaling/)?.[1];
        const newId = Object.keys(loadedSections).find(id => id.startsWith(sectionId)) || sectionId;
        if (newId && newId !== 'undefined' && loadedSections[newId]) {
          try {
            loadedSections[newId].totaling = JSON.parse(p.value) || { subtotal: false, equation: 'estimate_total' };
          } catch (e) {
            loadedSections[newId].totaling = { subtotal: false, equation: 'estimate_total' };
          }
        }
      }
      else if (p.key.match(/section_(.+)_background/) && p.value) {
        const sectionId = p.key.match(/section_(.+)_background/)?.[1];
        const realId = Object.keys(loadedSections).find(id => id.startsWith(sectionId)) || sectionId;
        if (realId && loadedSections[realId]) {
          try {
            const bg = JSON.parse(p.value);
            loadedSections[realId].background = {
              color: bg.color || '',
              image: bg.image || '',
            };
          } catch (e) { /* ignore */ }
        }
      }
      else if (p.key.match(/section_(.+)_header_color/)) {
        const sectionId = p.key.match(/section_(.+)_header_color/)?.[1];
        const newId = Object.keys(loadedSections).find(id => id.startsWith(sectionId)) || sectionId;
        if (newId && loadedSections[newId]) {
          loadedSections[newId].headerColor = p.value || '#f5f5f5';
        }
      }
      else if (p.key.match(/section_(.+)_header_font_color/)) {
        const sectionId = p.key.match(/section_(.+)_header_font_color/)?.[1];
        const newId = Object.keys(loadedSections).find(id => id.startsWith(sectionId)) || sectionId;
        if (newId && loadedSections[newId]) {
          loadedSections[newId].headerFontColor = p.value || '#000000';
        }
      }
      else if (p.key.match(/section_(.+)_font_color/)) {
        const sectionId = p.key.match(/section_(.+)_font_color/)?.[1];
        const newId = Object.keys(loadedSections).find(id => id.startsWith(sectionId)) || sectionId;
        if (newId && loadedSections[newId]) {
          loadedSections[newId].fontColor = p.value || '#000000';
        }
      }
      else if (p.key.match(/section_(.+)_items$/)) {
        const sectionId = p.key.match(/section_(.+)_items$/)?.[1];
        const newId = Object.keys(loadedSections).find(id => id.startsWith(sectionId)) || sectionId;
        if (newId && newId !== 'undefined') {
          if (!sectionItems[newId]) {
            sectionItems[newId] = [];
          }
          sectionItems[newId].push({
            id: generateUniqueId(),
            presetId: parseInt(p.value),
            propertyId: p.id,
            order: p.order,
          });
        }
      }
      else if (p.key.match(/section_(.+)_headers/)) {
        const sectionId = p.key.match(/section_(.+)_headers/)?.[1];
        const newId = Object.keys(loadedSections).find(id => id.startsWith(sectionId)) || sectionId;
        if (newId && p.value) {
          const pairs = p.value.split(',').filter(Boolean);
          loadedHeaders[newId] = pairs.map((pair, idx) => {
            const parts = pair.split(':');
            const field = parts[0]?.trim();
            const label = parts[1]?.trim() || field;
            const icon = parts[2]?.trim() || 'check-circle';
            const true_color = parts[3]?.trim() || '#4caf50';

            const iconProp = props.find(pr => pr.key === `section_${sectionId}_header_${idx}_icon`);
            const colorProp = props.find(pr => pr.key === `section_${sectionId}_header_${idx}_true_color`);
            const allowedProp = props.find(pr => pr.key === `section_${sectionId}_header_${idx}_allowed_profiles`);
            const inputTypeProp = props.find(pr => pr.key === `section_${sectionId}_header_${idx}_inputtype`);
            const includeInPrintProp = props.find(pr => pr.key === `section_${sectionId}_header_${idx}_include_in_print`);
            const printLabelProp = props.find(pr => pr.key === `section_${sectionId}_header_${idx}_print_label`);

            return {
              field,
              label,
              icon: iconProp?.value || icon,
              true_color: colorProp?.value || true_color,
              allowed_profiles: allowedProp?.value || '',
              inputtype: inputTypeProp?.value || getDefaultInputType(field),
              // Missing = printed, so existing forms keep every column on the PDF
              include_in_print: includeInPrintProp ? includeInPrintProp.value !== 'false' : true,
              print_label: printLabelProp?.value || '',
              order: p.order,
            };
          });
        }
      }
      else if (p.key === 'form_settings' && p.value) {
        try {
          const settings = JSON.parse(p.value);
          setEditingForm(prev => ({
            ...prev,
            form_settings: {
              ...(prev?.form_settings || {}),
              ...settings, // keep keys this editor doesn't manage (e.g. reportType)
              displayName: settings.displayName || '',
              backgroundColor: settings.backgroundColor || '',
              backgroundImage: settings.backgroundImage || '',
              showIconKey: settings.showIconKey !== false,
              sectionHeaderColor: settings.sectionHeaderColor || '',
              fontColor: settings.fontColor || '',
              sectionHeaderFontColor: settings.sectionHeaderFontColor || '',
            },
          }));
        } catch (e) { /* ignore */ }
      }
      else if (p.key === 'form_totaling' && p.value) {
        try {
          const parsed = JSON.parse(p.value);
          setFormTotaling({
            approved: {
              enabled: parsed.approved?.enabled ?? true,
              equation: parsed.approved?.equation || 'estimate_total',
              conditions: parsed.approved?.conditions || [{ header: 'approved', operator: 'equals', value: 'true' }],
              filters: parsed.approved?.filters || [],
            },
            completed: {
              enabled: parsed.completed?.enabled ?? true,
              equation: parsed.completed?.equation || 'estimate_total',
              conditions: parsed.completed?.conditions || [{ header: 'completed', operator: 'equals', value: 'true' }],
              filters: parsed.completed?.filters || [],
            },
            pending: {
              enabled: parsed.pending?.enabled ?? true,
              equation: parsed.pending?.equation || 'estimate_total',
              conditions: parsed.pending?.conditions || [
                { header: 'recommend', operator: 'equals', value: 'true' },
                { header: 'required', operator: 'equals', value: 'true', operatorType: 'or' },
                { header: 'approved', operator: 'equals', value: 'false' },
                { header: 'completed', operator: 'equals', value: 'false' },
              ],
              filters: parsed.pending?.filters || [],
            },
            grand: {
              enabled: parsed.grand?.enabled ?? true,
              equation: parsed.grand?.equation || 'estimate_total',
              conditions: parsed.grand?.conditions || [
                { header: 'recommend', operator: 'equals', value: 'true' },
                { header: 'required', operator: 'equals', value: 'true', operatorType: 'or' },
                { header: 'approved', operator: 'equals', value: 'true', operatorType: 'or' },
              ],
              filters: parsed.grand?.filters || [],
            },
            parts: {
              enabled: parsed.parts?.enabled ?? true,
              equation: parsed.parts?.equation || 'parts_total',
              conditions: parsed.parts?.conditions || [],
              filters: parsed.parts?.filters || [],
            },
            labor: {
              enabled: parsed.labor?.enabled ?? true,
              equation: parsed.labor?.equation || 'labor_total',
              conditions: parsed.labor?.conditions || [],
              filters: parsed.labor?.filters || [],
            },
          });
        } catch (e) {}
      }
      // NEW: Load send_sftp from custom_form_properties
      else if (p.key === 'send_sftp') {
        setSendSftp(p.value === 'true' || p.value === true || p.value === '1');
      }
      else if (p.key === 'print_criteria' && p.value) {
        setPrintCriteria(parsePrintCriteria(p.value));
      }
    });

    Object.keys(sectionItems).forEach(sectionId => {
      const newId = Object.keys(loadedSections).find(id => id.startsWith(sectionId)) || sectionId;
      if (loadedSections[newId]) {
        loadedSections[newId].items = sectionItems[newId].sort((a, b) => a.order - b.order);
      }
    });

    setHeaders(loadedHeaders);
    setSections(loadedSections);
    setSectionCounter(counter + 1);
    setIsDataReady(true);
  };

  const handleBack = () => {
    resetState();
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      if (!formName.trim()) {
        setError('Form name is required');
        return;
      }

      if (!editingForm?.id) {
        setError('Cannot save: form not loaded');
        return;
      }

      const formData = {
        name: formName,
        dealership: installation.subdomain,
        active: true,
        deleted: false,
      };

      const result = await dispatch(updateCustomForm(editingForm.id, formData));
      if (result.error) {
        setError('Failed to save form');
        return;
      }

      const oldProps = editingForm.custom_form_properties || [];
      await Promise.all(
        oldProps.map(p =>
          dispatch(deleteCustomFormPropertyRaw(p.id)).catch(err => null)
        )
      );

      const createPromises = [];
      let order = 1;

      const sectionOrder = Object.values(sections);
      sectionOrder.forEach((sec, sectionIdx) => {
        if (!sec || !sec.id || sec.id === 'undefined') return;

        const sectionId = sec.id;

        createPromises.push(
          dispatch(createCustomFormPropertyRaw({
            custom_form_id: editingForm.id,
            key: `section_${sectionId}_id`,
            value: sectionId,
            order: order++,
          }))
        );
        createPromises.push(
          dispatch(createCustomFormPropertyRaw({
            custom_form_id: editingForm.id,
            key: `section_${sectionId}_label`,
            value: sec.label || sectionId,
            order: order++,
          }))
        );

        const rule = sec.totaling || {};
        const totalingValue = JSON.stringify({
          subtotal: rule.subtotal || false,
          equation: rule.equation || 'estimate_total',
        });
        createPromises.push(
          dispatch(createCustomFormPropertyRaw({
            custom_form_id: editingForm.id,
            key: `section_${sectionId}_totaling`,
            value: totalingValue,
            order: order++,
          }))
        );

        const layoutValue = JSON.stringify(sec.layout || { width: '100%', align: 'left' });
        createPromises.push(
          dispatch(createCustomFormPropertyRaw({
            custom_form_id: editingForm.id,
            key: `section_${sectionId}_layout`,
            value: layoutValue,
            order: order++,
          }))
        );

        const bg = sec.background || { color: '', image: '' };
        createPromises.push(
          dispatch(createCustomFormPropertyRaw({
            custom_form_id: editingForm.id,
            key: `section_${sectionId}_background`,
            value: JSON.stringify({ color: bg.color || '', image: bg.image || '' }),
            order: order++,
          }))
        );

        createPromises.push(
          dispatch(createCustomFormPropertyRaw({
            custom_form_id: editingForm.id,
            key: `section_${sectionId}_header_color`,
            value: sec.headerColor || '',
            order: order++,
          }))
        );

        createPromises.push(
          dispatch(createCustomFormPropertyRaw({
            custom_form_id: editingForm.id,
            key: `section_${sectionId}_font_color`,
            value: sec.fontColor || '',
            order: order++,
          }))
        );

        createPromises.push(
          dispatch(createCustomFormPropertyRaw({
            custom_form_id: editingForm.id,
            key: `section_${sectionId}_header_font_color`,
            value: sec.headerFontColor || '',
            order: order++,
          }))
        );

        const hList = headers[sectionId] || [];
        if (hList.length > 0) {
          const headerString = hList
            .filter(h => h && h.field && h.label)
            .map(h => `${h.field}:${h.label}:${h.icon || 'check-circle'}:${h.true_color || '#4caf50'}`)
            .join(',');

          if (headerString) {
            createPromises.push(
              dispatch(createCustomFormPropertyRaw({
                custom_form_id: editingForm.id,
                key: `section_${sectionId}_headers`,
                value: headerString,
                order: order++,
              }))
            );

            hList.forEach((h, idx) => {
              if (h.icon) {
                createPromises.push(
                  dispatch(createCustomFormPropertyRaw({
                    custom_form_id: editingForm.id,
                    key: `section_${sectionId}_header_${idx}_icon`,
                    value: h.icon,
                    order: order++,
                  }))
                );
              }
              if (h.true_color) {
                createPromises.push(
                  dispatch(createCustomFormPropertyRaw({
                    custom_form_id: editingForm.id,
                    key: `section_${sectionId}_header_${idx}_true_color`,
                    value: h.true_color,
                    order: order++,
                  }))
                );
              }
              if (h.allowed_profiles) {
                createPromises.push(
                  dispatch(createCustomFormPropertyRaw({
                    custom_form_id: editingForm.id,
                    key: `section_${sectionId}_header_${idx}_allowed_profiles`,
                    value: h.allowed_profiles,
                    order: order++,
                  }))
                );
              }
              if (h.inputtype) {
                createPromises.push(
                  dispatch(createCustomFormPropertyRaw({
                    custom_form_id: editingForm.id,
                    key: `section_${sectionId}_header_${idx}_inputtype`,
                    value: h.inputtype,
                    order: order++,
                  }))
                );
              }
              createPromises.push(
                dispatch(createCustomFormPropertyRaw({
                  custom_form_id: editingForm.id,
                  key: `section_${sectionId}_header_${idx}_include_in_print`,
                  value: h.include_in_print === false ? 'false' : 'true',
                  order: order++,
                }))
              );
              if (h.print_label && h.print_label.trim()) {
                createPromises.push(
                  dispatch(createCustomFormPropertyRaw({
                    custom_form_id: editingForm.id,
                    key: `section_${sectionId}_header_${idx}_print_label`,
                    value: h.print_label.trim(),
                    order: order++,
                  }))
                );
              }
            });
          }
        }

        if (sec.items && Array.isArray(sec.items)) {
          sec.items.forEach(item => {
            if (item && item.presetId) {
              createPromises.push(
                dispatch(createCustomFormPropertyRaw({
                  custom_form_id: editingForm.id,
                  key: `section_${sectionId}_items`,
                  value: String(item.presetId),
                  order: order++,
                  id: item.propertyId
                }))
              );
            }
          });
        }
      });

      // Save form_settings
      const formSettings = editingForm?.form_settings || {};
      const formSettingsJson = JSON.stringify({
        ...formSettings, // keep keys this editor doesn't manage (e.g. reportType)
        displayName: formSettings.displayName || '',
        backgroundColor: formSettings.backgroundColor || '',
        backgroundImage: formSettings.backgroundImage || '',
        showIconKey: formSettings.showIconKey !== false,
        sectionHeaderColor: formSettings.sectionHeaderColor || '',
        fontColor: formSettings.fontColor || '',
        sectionHeaderFontColor: formSettings.sectionHeaderFontColor || '',
      });

      createPromises.push(
        dispatch(createCustomFormPropertyRaw({
          custom_form_id: editingForm.id,
          key: 'form_settings',
          value: formSettingsJson,
          order: order++,
        }))
      );

      // Save form_totaling
      const formTotalingValue = JSON.stringify(formTotaling);
      createPromises.push(
        dispatch(createCustomFormPropertyRaw({
          custom_form_id: editingForm.id,
          key: 'form_totaling',
          value: formTotalingValue,
          order: order++,
        }))
      );

      // NEW: Save send_sftp flag
      createPromises.push(
        dispatch(createCustomFormPropertyRaw({
          custom_form_id: editingForm.id,
          key: 'send_sftp',
          value: sendSftp.toString(),
          order: order++,
        }))
      );

      // print_criteria: omitted when there are no rules (report default applies)
      const printRules = (printCriteria.rules || []).filter(r => r.field);
      if (printRules.length) {
        createPromises.push(
          dispatch(createCustomFormPropertyRaw({
            custom_form_id: editingForm.id,
            key: 'print_criteria',
            value: JSON.stringify({ match_mode: printCriteria.match_mode || 'any', rules: printRules }),
            order: order++,
          }))
        );
      }

      await Promise.all(createPromises);
      await dispatch(getCustomForms());
      handleBack();
    } catch (err) {
      setError(`Failed to save properties: ${err.message}`);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (formId) => {
    if (window.confirm('Delete form?')) {
      await dispatch(deleteCustomForm(formId));
      await dispatch(getCustomForms());
    }
  };

  const openNewFormModal = () => {
    setNewFormName('');
    setNewFormModalOpen(true);
  };

  const closeNewFormModal = () => {
    setNewFormModalOpen(false);
    setNewFormName('');
  };

  const handleCreateNewForm = async () => {
    if (!newFormName.trim()) {
      setError('Form name is required');
      return;
    }

    const formData = {
      name: newFormName,
      dealership: installation.subdomain,
      active: true,
      deleted: false,
    };

    const result = await dispatch(createCustomForm(formData));
    if (result.error) {
      setError('Failed to create form');
      return;
    }

    setEditingForm(result.payload);
    setFormName(newFormName);
    setSections({});
    setHeaders({});
    setSendSftp(true);
    setPrintCriteria(DEFAULT_PRINT_CRITERIA);
    setSectionCounter(0);
    setIsDataReady(true);
    await dispatch(getCustomForms());
    closeNewFormModal();
  };

  const openAddSectionModal = () => {
    setNewSectionName('');
    setSectionModalOpen(true);
  };

  const closeAddSectionModal = () => {
    setSectionModalOpen(false);
    setNewSectionName('');
  };

  const handleAddSection = () => {
    if (!newSectionName.trim()) {
      setError('Section name is required');
      return;
    }

    const existingIds = Object.keys(sections);
    const newId = sanitizeLabelForId(newSectionName, existingIds, sectionCounter);
    setSections(prev => ({
      ...prev,
      [newId]: {
        label: newSectionName,
        id: newId,
        items: [],
        totaling: { subtotal: false, equation: 'estimate_total' },
        layout: { width: '100%', align: 'left' },
        background: { color: '', image: '' },
        headerColor: '#f5f5f5',
        fontColor: '#000000',
        headerFontColor: '#000000',
      },
    }));
    setHeaders(prev => ({ ...prev, [newId]: [] }));
    setSectionCounter(prev => prev + 1);
    setIsDataReady(true);
    closeAddSectionModal();
  };

  return {
    custom_forms,
    editingForm,
    setEditingForm,
    formName,
    setFormName,
    sections,
    setSections,
    headers,
    setHeaders,
    formTotaling,
    setFormTotaling,
    sendSftp,           // NEW
    setSendSftp,        // NEW
    printCriteria,
    setPrintCriteria,
    sectionCounter,
    setSectionCounter,
    error,
    setError,
    sectionModalOpen,
    setSectionModalOpen,
    newSectionName,
    setNewSectionName,
    newFormModalOpen,
    setNewFormModalOpen,
    newFormName,
    setNewFormName,
    saving,
    isDataReady,
    tableRefs,
    handleEdit,
    handleBack,
    handleSave,
    handleDelete,
    openNewFormModal,
    closeNewFormModal,
    handleCreateNewForm,
    openAddSectionModal,
    closeAddSectionModal,
    handleAddSection,
    default_profiles,
  };
};

export default useFormEditor;